// Text helpers shared by search and parsing. Mirrors Swift's
// `.folding([.diacriticInsensitive, .caseInsensitive])` + alphanumeric tokenising.

/** Lowercase and strip diacritics ("Crème Brûlée" → "creme brulee"). */
export function fold(text: string): string {
  try {
    return text.normalize('NFD').replace(/\p{M}/gu, '').normalize('NFC').toLowerCase();
  } catch {
    return text.toLowerCase(); // engines without String.prototype.normalize
  }
}

const ALNUM = /[\p{L}\p{N}]/u;

/** Folded text with every non-alphanumeric run collapsed to one space, trimmed. */
export function normalize(text: string): string {
  let out = '';
  for (const ch of fold(text)) out += ALNUM.test(ch) ? ch : ' ';
  return out.split(' ').filter(Boolean).join(' ');
}

export function isLetter(ch: string): boolean {
  return /\p{L}/u.test(ch);
}

export function isDigit(ch: string): boolean {
  return /\p{Nd}/u.test(ch);
}

/** Character count like Swift's String.count (code points; fine after folding). */
export function len(s: string): number {
  return [...s].length;
}

/** Levenshtein with early exit; an adjacent swap counts as 1 (same as the Swift code). */
export function levenshtein(a: string, b: string, limit: number): number {
  const left = [...a];
  const right = [...b];
  if (limit >= 1 && isSingleAdjacentTransposition(left, right)) return 1;
  if (Math.abs(left.length - right.length) > limit) return limit + 1;
  let previous = Array.from({ length: right.length + 1 }, (_, i) => i);
  for (let i = 1; i <= left.length; i++) {
    const current = [i, ...new Array<number>(right.length).fill(0)];
    let rowMin = current[0];
    for (let j = 1; j <= right.length; j++) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + cost);
      rowMin = Math.min(rowMin, current[j]);
    }
    if (rowMin > limit) return limit + 1;
    previous = current;
  }
  return previous[right.length];
}

function isSingleAdjacentTransposition(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const mismatches: number[] = [];
  for (let i = 0; i < left.length; i++) if (left[i] !== right[i]) mismatches.push(i);
  if (mismatches.length !== 2 || mismatches[1] !== mismatches[0] + 1) return false;
  const [f, s] = mismatches;
  return left[f] === right[s] && left[s] === right[f];
}

/** 0 edits for words ≤4 chars, 1 for 5–8, 2 for ≥9. */
export function typoAllowance(length: number): number {
  if (length >= 9) return 2;
  return length >= 5 ? 1 : 0;
}

export function uniq<T>(items: T[]): T[] {
  return [...new Set(items)];
}
